import { Component, OnInit, Optional } from '@angular/core';
import { TreeNode } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { FilesService } from '../../../../../services/files.service';
import { FileInfoFactory } from '../../../../models/file-info/file-info.factory';
import { IFileInfo, IFile, RawFileInfo } from '../../../../models/file-info';
import { StorageKey } from '../../../../models/request';
import { PAGE_SIZE } from '../../../../main.utilities';

/**
 * A second, independent file tree for picking a script out of storage - sets
 * `QupathScriptRequest.scriptKey`. Deliberately NOT `FileTreeComponent`: that component is a
 * singleton wired to `MainFacade`'s one globally-selected-file state (every other tool reads the
 * same selection for its input image), and a script composer needs a *second*, unrelated
 * selection open at the same time - see jit-ui#138. This reuses `FilesService`'s plain lazy-load
 * methods (`getProjects`/`getChildren`) directly, with its own local `nodes` array, rather than
 * the component.
 *
 * Folders page the same way the sidebar does (`MainFacade.loadChildren`): a full page of
 * `PAGE_SIZE` children is followed by a "More..." sentinel node that fetches the next page.
 */
@Component({
  selector: 'storage-script-picker',
  templateUrl: './storage-script-picker.component.html',
  styleUrls: ['./storage-script-picker.component.scss'],
})
export class StorageScriptPickerComponent implements OnInit {

  public readonly IFile = IFile;

  nodes: TreeNode<IFileInfo>[] = [];
  loading = false;
  selectedNode: TreeNode<IFileInfo> | null = null;

  // Folders whose first page has been requested - an empty folder has no children to tell it
  // apart from one that was never expanded.
  private loadedNodes = new Set<TreeNode<IFileInfo>>();

  constructor(
    private filesService: FilesService,
    private ref: DynamicDialogRef,
    // Optional so a hand-built picker (unit test) needs no dialog config.
    @Optional() private config?: DynamicDialogConfig,
  ) {}

  /** Lowercase file extensions (with the dot) this picker accepts, or empty for any file. */
  private get extensions(): string[] {
    return ((this.config?.data?.extensions as string[] | undefined) ?? []).map((e) => e.toLowerCase());
  }

  ngOnInit(): void {
    this.loading = true;
    this.filesService.getProjects().subscribe({
      next: (raw: RawFileInfo[]) => {
        const projects = raw.map((r: RawFileInfo) => FileInfoFactory.getFileInfo(r));
        this.nodes = this.filesService.createNodes(projects);
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  onNodeExpand(event: { node: TreeNode<IFileInfo> }): void {
    const node = event.node;
    if (!node.data || this.loadedNodes.has(node)) return;
    this.loadedNodes.add(node);
    this.loadPage(node, 0);
  }

  onLoadMore(event: Event, sentinel: TreeNode): void {
    event.stopPropagation();
    const folder = sentinel.parent as TreeNode<IFileInfo> | undefined;
    if (!folder?.data) return;
    sentinel.type = 'loadingmore';
    this.loadPage(folder, sentinel.data.nextPage);
  }

  private loadPage(node: TreeNode<IFileInfo>, page: number): void {
    node.loading = true;
    this.filesService.getChildren(node.data!.rawData, page, PAGE_SIZE).subscribe({
      next: (raw: RawFileInfo[]) => {
        node.children = (node.children ?? []).filter((c) => c.type !== 'loadmore' && c.type !== 'loadingmore');
        const files = raw.map((f: RawFileInfo) => FileInfoFactory.getFileInfo(f));
        const newNodes = this.filesService.createNodes(files);
        newNodes.forEach((n: TreeNode<IFileInfo>) => (n.parent = node));
        node.children.push(...newNodes);
        if (raw.length === PAGE_SIZE) {
          const sentinel = this.filesService.createLoadMoreNode(node.data!.rawData, page + 1);
          sentinel.parent = node;
          node.children.push(sentinel as TreeNode<IFileInfo>);
        }
        node.loading = false;
      },
      error: () => {
        // Drop the spinner sentinel back to a clickable "More..." so the user can retry; a failed
        // first page is un-marked so collapsing and re-expanding the folder tries again.
        node.children?.forEach((c) => {
          if (c.type === 'loadingmore') c.type = 'loadmore';
        });
        if (page === 0) this.loadedNodes.delete(node);
        node.loading = false;
      },
    });
  }

  isSelectableFile(node: TreeNode<IFileInfo> | null): boolean {
    if (!node?.data || !(node.data instanceof IFile)) return false;
    const extensions = this.extensions;
    if (extensions.length === 0) return true;
    const name = (node.data.name ?? node.data.relPath ?? '').toLowerCase();
    return extensions.some((e) => name.endsWith(e));
  }

  onChoose(): void {
    const data = this.selectedNode?.data;
    if (!data || !(data instanceof IFile) || !this.isSelectableFile(this.selectedNode)) return;
    const key: StorageKey = { bucket: data.project.bucket, object: data.relPath };
    this.ref.close(key);
  }

  onCancel(): void {
    this.ref.close(undefined);
  }
}
